import { CountryChangeRequestCreateDTO, CountryChangeRequestsClient, ICountryChangeRequestCreateDTO } from 'api/swagger/ApiClient';
import { deepComputedFrom } from 'aurelia-deep-computed';
import { autoinject, computedFrom, newInstance } from 'aurelia-framework';
import { Redirect, RouteConfig, Router } from 'aurelia-router';
import { ValidationController } from 'aurelia-validation';
import { watch } from 'aurelia-watch-decorator';
import { DialogHelper } from 'framework/aurelia_dialogs/dialog-helper';
import { BootstrapGroupRenderer } from 'framework/aurelia_tools/bootstrap-group-renderer';
import { log } from 'framework/script_utilities/dev-decorators';
import { Country } from 'model/business-keywords';
import { UserContact } from 'model/business-types';
import { CountryChangeRequestData, CountryChangeRequestModel } from 'model/forms/country-change-request-data';
import { SystemUserWrapper } from 'model/support-types';
import { EnumsService } from 'services/enums-service';
import { IdentityService } from 'services/identity-service';
import { KeywordsService } from 'services/keywords-service';
import { LookupsService } from 'services/lookups-service';
import { SafetyService } from 'services/safety-service';
import { requireServices } from 'support/activation';
import { ToastFormat, ToastHelper, ToastType } from 'support/toaster';
import { ValidationHelper } from 'support/validation';

@autoinject
export class AdminCountryChangeRequestCreateScreen {
  country: Country | null;
  model: CountryChangeRequestModel = null;
  submitButtonVisible = false;
  cancelButtonVisible = false;
  editing = true;
  loaded = false;
  changed = false;
  approversEditing = false;
  route: RouteConfig = null;

  constructor(private api: CountryChangeRequestsClient, private keywords: KeywordsService, private enums: EnumsService, private lookups: LookupsService, private safety: SafetyService, private identity: IdentityService, private router: Router, private dialogHelper: DialogHelper, private toastHelper: ToastHelper, private validationHelper: ValidationHelper, @newInstance() private validationController: ValidationController, validationRenderer: BootstrapGroupRenderer) {
    validationRenderer.allowedParents = 'bs-form-input, bs-form-lookup, bs-form-select, bs-form-textarea';
    this.validationController.addRenderer(validationRenderer);
  }

  async canActivate() {
    await requireServices(this.keywords, this.lookups, this.enums, this.safety);
  }

  @log('debug')
  async activate(params, route: RouteConfig) {
    this.route = route;
  }

  async canDeactivate() {
    if (this.editing && this.changed && !await this.dialogHelper.confirm({ dialogTitle: 'Leave without saving?', message: 'You haven\'t submitted the request yet. If you proceed, your changes will be lost!', okTitle: 'Leave without saving', cancelTitle: 'Stay here!' }))
      return new Redirect((this.router.history as any).previousLocation, { trigger: false, replace: false });
  }

  @deepComputedFrom('keywords.countries', 'country')
  get countries() {
    return this.keywords.ensureItem(this.keywords.countries, this.country);
  }

  @watch((x: AdminCountryChangeRequestCreateScreen) => x.country)
  bindDetail() {
    if (this.country != null) {
      const countrySettings = this.safety.countrySettings.get(this.country.id);
      this.model = {
        country: this.country,
        effectiveFrom: null,
        requestedBy: this.identity.user as UserContact,
        note: null,
        data: {
          approvalLevel: countrySettings.approvalLevel,
          approvalRequirement: this.enums.approvalRequirements.find(x => x.value === countrySettings.approvalRequirementValue),
          approveType: this.enums.approvalTypes.find(x => x.value === countrySettings.approvalTypeValue),
          approversWrapper: countrySettings.approvers.map(x => ({ user: { uniqueid: x.uniqueid, username: x.username, name: x.name, mail: x.mail, jobTitle: x.jobTitle, phone: x.phone } as UserContact, userId: null } as SystemUserWrapper))
        } as CountryChangeRequestData,
        changes: []
      } as CountryChangeRequestModel;
      this.loaded = true;
    }
    else {
      this.loaded = false;
    }
  }

  @watch((x: AdminCountryChangeRequestCreateScreen) => x.editing)
  @watch((x: AdminCountryChangeRequestCreateScreen) => x.changed)
  private updateButtons() {
    this.submitButtonVisible = this.editing && this.changed;
    this.cancelButtonVisible = this.editing && this.changed;
  }

  async submit() {
    if (!this.approversEditing || await this.dialogHelper.confirm({ dialogTitle: 'Form contains unconfirmed data.', message: 'You haven\'t confirmed newly added Approver. If you proceed, your changes will be lost!', okTitle: 'Sure, do it!', cancelTitle: 'Back to form' })) {
      await this.validationHelper.validate(this.validationController) && await this.dialogHelper.confirm({ dialogTitle: 'Please check your changes.', message: `You are requesting following changes:\n${this.model.changes.join('\n')}\n\nAre you sure you want to continue?`, okTitle: 'Sure, do it!', cancelTitle: 'Back to form' }) && await this.save();
    }
  }

  async cancel() {
    if (!this.changed || await this.dialogHelper.confirm({ dialogTitle: 'Reset your changes?', message: 'If you proceed, your changes will be reset to country\'s current settings!', okTitle: 'Sure, do it!', cancelTitle: 'Back to form' })) {
      this.changed && this.bindDetail();
    }
  }

  private async save() {
    const data = this.getSaveData(this.model);
    await this.api.createCountryChangeRequest(new CountryChangeRequestCreateDTO(data))
    this.changed = false;
    this.toastHelper.toast('Country change requested has been successfully <b>submitted</b>.', 'Submitted', ToastType.Success, ToastFormat.Html);
    this.router.navigateToRoute('home');
  }

  getSaveData(model: CountryChangeRequestModel): ICountryChangeRequestCreateDTO {
    return {
      countryId: model.country.id,
      approvalLevelId: model.data.approvalLevel.id,
      approvalRequirement: model.data.approvalRequirement.value,
      approversUniqueIds: model.data.approversWrapper.map(x => x.user.uniqueid),
      approveType: model.data.approveType.value,
      effectiveFrom: model.effectiveFrom,
      note: model.note,
      requestedByUniqueId: model.requestedBy.uniqueid
    }
  }

  @computedFrom('model.country.name', 'model.country.formFlag')
  get titleWithIcon() {
    const title = this.model?.country.name == null ? 'New Change Request' : `${this.model.country.name} Change Request`;
    return `<i class="${(this.model != null && this.model.country.formFlag != null ? this.model.country.formFlag : '')}" style="margin-right: 5px;"></i><span>${title}</span>`;
  }

  @watch((x: AdminCountryChangeRequestCreateScreen) => x.model)
  private modelChanged() {
    this.validationController.reset();
  }
}
